/**
 * Extract an array of items from any backend list response shape.
 *
 * The backend may return:
 *  - PagedResponse: { items: T[], total, page, per_page, pages }
 *  - Wrapped array:  { data: T[] }
 *  - Named key:      { users: T[] } / { organizations: T[] } / etc.
 *  - Bare array:     T[]
 *
 * This helper normalises all shapes into a plain T[].
 */
export function extractItems<T>(response: any): T[] {
    const data = response?.data
    if (!data) return []
    if (Array.isArray(data)) return data
    if (data.items && Array.isArray(data.items)) return data.items
    if (data.data && Array.isArray(data.data)) return data.data
    // fallback: first array-valued key
    for (const key of Object.keys(data)) {
        if (Array.isArray(data[key])) return data[key]
    }
    return []
}

/**
 * Extract pagination metadata from a PagedResponse.
 * Returns null when the response has no pagination info.
 */
export function extractPagination(response: any) {
    const data = response?.data
    if (!data || typeof data !== 'object') return null
    if ('total' in data && 'page' in data) {
        return {
            total: data.total as number,
            page: data.page as number,
            per_page: data.per_page as number,
            pages: data.pages as number,
        }
    }
    return null
}
